import { type ReactElement, useCallback, useMemo, useState } from 'react';

import { ContentDataProvider } from './contentData';
import { GhaDataProvider } from './ghaData/GhaDataProvider';
import { GlobalDataProvider } from './globalData/GlobalDataProvider';
import { AdDataContext } from './shared/context';
import { StoresDataProvider } from './storesData/StoresDataProvider';
import { type AdDataProviderProps, ConfigName } from './types';
import { useContentUrl } from './useContentUrl';

export * from './types';

export function AdDataProvider({
  children,
  contentUrl,
  globalUrl,
  locationsUrl,
  locationsLightUrl,
  storesUrl,
}: Readonly<AdDataProviderProps>): ReactElement {
  const [loadGlobal, setLoadGlobal] = useState<boolean>(false);
  const [loadGha, setLoadGha] = useState<boolean>(false);
  const [loadStores, setLoadStores] = useState<boolean>(false);
  const [loadContent, setLoadContent] = useState<boolean>(false);
  const [chosenContentUrl, setChosenContentUrl] = useState(contentUrl);

  const getChosenContentUrl = useContentUrl(contentUrl);

  const switchContentUrl = useCallback(
    (id: string): void => setChosenContentUrl(getChosenContentUrl(id)),
    [getChosenContentUrl],
  );

  const ghaWrap = <GhaDataProvider load={loadGha}>{children}</GhaDataProvider>;
  const contentWrap = (
    <ContentDataProvider
      locationsUrl={locationsUrl}
      locationsLightUrl={locationsLightUrl}
      url={chosenContentUrl}
      load={loadContent}
    >
      {ghaWrap}
    </ContentDataProvider>
  );
  const storesWrap = (
    <StoresDataProvider url={storesUrl} load={loadStores}>
      {contentWrap}
    </StoresDataProvider>
  );
  const globalWrap = (
    <GlobalDataProvider url={globalUrl} load={loadGlobal}>
      {storesWrap}
    </GlobalDataProvider>
  );

  const setData = useMemo(
    () => ({
      [ConfigName.GLOBAL]: () => {
        if (!loadGlobal) {
          setLoadGlobal(true);
        }
      },
      [ConfigName.STORES]: () => {
        if (!loadStores) {
          setLoadStores(true);
        }
      },
      [ConfigName.CONTENT]: () => {
        if (!loadContent) {
          setLoadContent(true);
        }
      },
      [ConfigName.GHA]: () => {
        if (!loadGha) {
          setLoadGha(true);
        }
      },
    }),
    [loadGlobal, loadStores, loadContent, loadGha],
  );

  const ctxValues = useMemo(
    () => ({
      operations: {
        setContentUrl: switchContentUrl,
        setFullContentUrl: setChosenContentUrl,
        setData,
      },
    }),
    [switchContentUrl, setData],
  );

  return <AdDataContext.Provider value={ctxValues}>{globalWrap}</AdDataContext.Provider>;
}
